import React, { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2Icon, ClockIcon, AlertTriangleIcon, EyeIcon, PlusIcon, InboxIcon, ArrowRightIcon, ClipboardListIcon, LayersIcon } from "lucide-react";
import { TopBar } from "./TopBar";
import { ClickHint } from "./ClickHint";
import { ELM, MAIN, BRAND } from "../lib/data";
type DeptKey = "concierge" | "luxury" | "support";
type StageKey = "ordered" | "process" | "review" | "ready";
type Order = {
  id: string;
  agent: string;
  address: string;
  thumb: string;
  pkg: string;
  status: string;
  statusTone: "progress" | "review" | "done" | "overdue";
  eta: string;
  dept: DeptKey;
  stage: StageKey;
  overdue?: boolean;
  highlight?: boolean;
};
const statusStyles: Record<Order["statusTone"], string> = {
  progress: "bg-blue-50 text-blue-700",
  review: "bg-amber-50 text-amber-700",
  done: "bg-emerald-50 text-emerald-700",
  overdue: "bg-red-50 text-red-700"
};
const deptLabels: Record<DeptKey, string> = {
  concierge: "Listing Concierge",
  luxury: "Luxury Marketing",
  support: "Customer Support"
};
const ORDERS: Order[] = [{
  id: "o-1042",
  agent: "Bob Carter",
  address: ELM.address,
  thumb: ELM.hero,
  pkg: "Marketing Package",
  status: "In review",
  statusTone: "review",
  eta: "ETA: Thu, 2:00 PM",
  dept: "concierge",
  stage: "review",
  highlight: true
}, {
  id: "o-201",
  agent: "Dana Lopez",
  address: "88 Oak Avenue",
  thumb: ELM.photos[1],
  pkg: "Social Ad Package",
  status: "In design",
  statusTone: "progress",
  eta: "ETA: Wed, 11:00 AM",
  dept: "concierge",
  stage: "process"
}, {
  id: "o-202",
  agent: "Priya Shah",
  address: "5 Birch Lane",
  thumb: ELM.photos[2],
  pkg: "Print + Social Bundle",
  status: "Ready to deliver",
  statusTone: "done",
  eta: "Delivered today",
  dept: "concierge",
  stage: "ready"
}, {
  id: "o-210",
  agent: "Marcus Reed",
  address: "1 Lakeview Estate",
  thumb: ELM.photos[3],
  pkg: "Luxury Signature Package",
  status: "Photography booked",
  statusTone: "progress",
  eta: "ETA: Fri, 3:00 PM",
  dept: "luxury",
  stage: "process"
}, {
  id: "o-215",
  agent: "Sofia Marin",
  address: "9 Harbor View",
  thumb: ELM.photos[0],
  pkg: "Luxury Launch Package",
  status: "Order received",
  statusTone: "progress",
  eta: "Starting within 24h",
  dept: "luxury",
  stage: "ordered"
}, {
  id: "o-220",
  agent: "Nina Patel",
  address: "42 Willow Court",
  thumb: MAIN.hero,
  pkg: "Seller Report Delivery",
  status: "Past SLA — escalated to Customer Support",
  statusTone: "overdue",
  eta: "Overdue by 6h",
  dept: "support",
  stage: "review",
  overdue: true
}];
const DEPT_LANES: {
  key: DeptKey | "soon";
  title: string;
  desc: string;
  comingSoon?: boolean;
}[] = [{
  key: "concierge",
  title: "Listing Concierge",
  desc: "Standard listing marketing"
}, {
  key: "luxury",
  title: "Luxury Marketing",
  desc: "Premium & luxury tier"
}, {
  key: "support",
  title: "Customer Support",
  desc: "Escalations & help"
}, {
  key: "soon",
  title: "Coming Soon",
  desc: "New service lane",
  comingSoon: true
}];
const STAGE_LANES: {
  key: StageKey;
  title: string;
  desc: string;
}[] = [{
  key: "ordered",
  title: "Ordered",
  desc: "New, not yet started"
}, {
  key: "process",
  title: "In Process",
  desc: "In production"
}, {
  key: "review",
  title: "Review",
  desc: "Awaiting sign-off"
}, {
  key: "ready",
  title: "Ready",
  desc: "Delivered / complete"
}];
const REQUESTS = [{
  id: "r1",
  label: "Get ETA on Lakeview Estate request",
  meta: "1 Lakeview Estate · Luxury Marketing",
  action: "Send ETA"
}, {
  id: "r2",
  label: "More information from client",
  meta: "42 Willow Court · Customer Support",
  action: "Message client"
}, {
  id: "r3",
  label: "Confirm launch window",
  meta: "12 Elm Street · Listing Concierge",
  action: "Confirm"
}];
type GroupMode = "department" | "stage";
export function OperationsBoard({
  onBack,
  onHome,
  onOpenOrder
}: {
  onBack: () => void;
  onHome: () => void;
  onOpenOrder: () => void;
}) {
  const [mode, setMode] = useState<GroupMode>("department");
  return <div className="min-h-full w-full bg-neutral-50 text-neutral-900" data-id="element-442" data-mp="3grykjqn2t4rr">
      <TopBar onHome={onHome} onBack={onBack} backLabel="Analytics" title="Order Routing" data-id="element-443" data-mp="3cr4uorq2pfw8" />

      <div className="mx-auto max-w-[1400px] px-5 py-6 sm:px-8" data-id="element-444" data-mp="db80i7unsr72">
        <div className="flex flex-col gap-1" data-id="element-445" data-mp="8bvaje8hr409">
          <h1 className="font-heading text-2xl font-bold tracking-tight" data-id="element-446" data-mp="8d6fhhdoahsy">Order routing board</h1>
          <p className="text-[15px] text-neutral-500" data-id="element-447" data-mp="3ju448y087i5">
            Every order in one shared queue — grouped the way your team works.
          </p>
        </div>

        {/* Admin requests / action inbox */}
        <section className="mt-5 overflow-hidden rounded-2xl border border-neutral-200 bg-white" data-id="element-448" data-mp="w30qruicfyb3">
          <div className="flex items-center gap-3 border-b border-neutral-100 px-5 py-3" data-id="element-449" data-mp="r96a6zy1bzg8">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg text-white" style={{
            backgroundColor: BRAND.accent
          }} data-id="element-450" data-mp="29ysmt6xyyv1z">
              <InboxIcon className="h-4 w-4" data-id="element-451" data-mp="2eyf0tilmgfbu" />
            </span>
            <span className="text-sm font-semibold text-neutral-900" data-id="element-452" data-mp="2ixe4kzvma61p">Requests needing action</span>
            <span className="ml-auto rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-semibold text-neutral-600" data-id="element-453" data-mp="2ny1qj74znwp2">{REQUESTS.length} open</span>
          </div>
          <ul className="divide-y divide-neutral-100" data-id="element-454" data-mp="2xv6d382qxu6e">
            {REQUESTS.map(req => <li key={req.id} className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-455" data-mp="32merkiixo5qf">
                <div className="min-w-0" data-id="element-456" data-mp="36nzzdpgm1ttr">
                  <p className="text-sm font-semibold text-neutral-900" data-id="element-457" data-mp="36pukb3rvtlxu">{req.label}</p>
                  <p className="text-xs text-neutral-500" data-id="element-458" data-mp="3boe5h1ynitp5">{req.meta}</p>
                </div>
                <button className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 transition hover:bg-neutral-50 sm:self-auto" data-id="element-459" data-mp="3gmfpbp3wix1o">
                  {req.action} <ArrowRightIcon className="h-3.5 w-3.5" data-id="element-460" data-mp="1qaj8gi2ujw0h" />
                </button>
              </li>)}
          </ul>
        </section>

        {/* Filter toggle + legend */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-461" data-mp="1z9eh00td5i6y">
          <div className="inline-flex rounded-xl border border-neutral-200 bg-white p-1 shadow-sm" data-id="element-462" data-mp="1v9cddm42iep1">
            <ToggleTab active={mode === "department"} onClick={() => setMode("department")} icon={<LayersIcon className="h-4 w-4" data-id="element-464" data-mp="248r1wg0ocf6w" />} label="Filter by department" data-id="element-463" data-mp="298luco03lcu2" />
            <ToggleTab active={mode === "stage"} onClick={() => setMode("stage")} icon={<ClipboardListIcon className="h-4 w-4" data-id="element-466" data-mp="eukubzstajse" />} label="Filter by order stage" data-id="element-465" data-mp="eub3k9zy08ln" />
          </div>
          <span className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-600" data-id="element-467" data-mp="9x2m6bmmc8lg">
            <EyeIcon className="h-3.5 w-3.5" style={{
            color: BRAND.accentText
          }} data-id="element-468" data-mp="2qhxvyf7wilge" />
            All departments see all orders — one queue, full visibility.
          </span>
        </div>

        {/* Kanban lanes */}
        {mode === "department" ? <BoardGrid data-id="element-469" data-mp="2gpib9a57zirt">
            {DEPT_LANES.map(lane => <Lane key={lane.key} title={lane.title} desc={lane.desc} comingSoon={lane.comingSoon} orders={lane.comingSoon ? [] : ORDERS.filter(o => o.dept === lane.key)} onOpenOrder={onOpenOrder} data-id="element-470" data-mp="22rakxl2ovfpm" />)}
          </BoardGrid> : <BoardGrid data-id="element-471" data-mp="3n86uhx93jr4o">
            {STAGE_LANES.map(lane => <Lane key={lane.key} title={lane.title} desc={lane.desc} orders={ORDERS.filter(o => o.stage === lane.key)} onOpenOrder={onOpenOrder} data-id="element-472" data-mp="3n5ua7dfv94og" />)}
          </BoardGrid>}
      </div>
    </div>;
}
function BoardGrid({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-473" data-mp="2660qykrzc4nr">{children}</div>;
}
function ToggleTab({
  active,
  onClick,
  icon,
  label
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return <button onClick={onClick} className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition ${active ? "text-white shadow-sm" : "text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"}`} style={active ? {
    backgroundColor: BRAND.accent
  } : undefined} data-id="element-474" data-mp="1s83tlm5pbu81">
      {icon} {label}
    </button>;
}
function Lane({
  title,
  desc,
  orders,
  comingSoon,
  onOpenOrder
}: {
  title: string;
  desc: string;
  orders: Order[];
  comingSoon?: boolean;
  onOpenOrder: () => void;
}) {
  return <div className={`flex min-h-[220px] flex-col rounded-2xl border p-3 ${comingSoon ? "border-dashed border-neutral-300 bg-neutral-100/50" : "border-neutral-200 bg-neutral-100/60"}`} data-id="element-475" data-mp="3k6x2aewr6szi">
      <div className="flex items-center justify-between px-1.5 pb-3 pt-1" data-id="element-476" data-mp="3tykoo1egdju4">
        <div data-id="element-477" data-mp="2ri0zjxtpvon">
          <h3 className="text-sm font-semibold text-neutral-900" data-id="element-478" data-mp="2px4hory82o5">{title}</h3>
          <p className="text-xs text-neutral-500" data-id="element-479" data-mp="3193zdcm4qj24">{desc}</p>
        </div>
        {!comingSoon && <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-white px-1.5 text-xs font-semibold text-neutral-600" data-id="element-480" data-mp="3675k01z7eqfp">{orders.length}</span>}
      </div>

      <div className="flex flex-col gap-2.5" data-id="element-481" data-mp="3g5uyrlvszurq">
        {orders.map(order => order.highlight ? <ClickHint key={order.id} onClick={onOpenOrder} label="Open" rounded="rounded-xl" className="w-full" data-id="element-482" data-mp="qh2h7v2leeu8">
              <OrderCard order={order} data-id="element-483" data-mp="veui3el7tbak" />
            </ClickHint> : <OrderCard key={order.id} order={order} onClick={onOpenOrder} data-id="element-484" data-mp="10vmsoc145nzu" />)}
        {comingSoon && <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-neutral-300 py-10 text-center" data-id="element-485" data-mp="14vwvjftlzyah">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-neutral-400" data-id="element-486" data-mp="19prl2zvwb7hh">
              <PlusIcon className="h-4 w-4" data-id="element-487" data-mp="hwijn39knu86" />
            </span>
            <p className="text-xs font-medium text-neutral-400" data-id="element-488" data-mp="hy3eyv3pnh73">New service lane<br data-id="element-489" data-mp="lxc8pzyr3f2n" />coming soon</p>
          </div>}
        {!comingSoon && !orders.length && <div className="rounded-xl border border-dashed border-neutral-300 bg-white/40 px-4 py-8 text-center text-xs text-neutral-400" data-id="element-490" data-mp="qwxf2vg92ppe">No orders here</div>}
      </div>
    </div>;
}
function OrderCard({
  order,
  onClick
}: {
  order: Order;
  onClick?: () => void;
}) {
  const clickable = !!onClick;
  return <motion.div onClick={onClick} whileTap={clickable ? {
    scale: 0.98
  } : undefined} role={clickable ? "button" : undefined} className={`rounded-xl border bg-white p-3 shadow-sm transition ${clickable ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-md" : ""}`} style={order.highlight ? {
    borderColor: BRAND.accent + "55"
  } : {
    borderColor: "#e5e5e5"
  }} data-id="element-491" data-mp="27a5h2oy105j7">
      <div className="flex items-start gap-3" data-id="element-492" data-mp="3d3tp6s0xfpzh">
        <img src={order.thumb} alt={order.address} className="h-12 w-12 shrink-0 rounded-lg object-cover" data-id="element-493" data-mp="392zyjqw3nx2w" />
        <div className="min-w-0 flex-1" data-id="element-494" data-mp="3446lfzkq6839">
          <p className="truncate text-sm font-semibold text-neutral-900" data-id="element-495" data-mp="3433w3w31wdpg">{order.address}</p>
          <p className="truncate text-xs text-neutral-500" data-id="element-496" data-mp="2z5vdxvhwk9o7">{order.agent}</p>
          <p className="mt-0.5 truncate text-xs font-medium text-neutral-700" data-id="element-497" data-mp="2u68wf9v1k79l">{order.pkg}</p>
        </div>
      </div>

      <div className="mt-3" data-id="element-498" data-mp="xce7gv5idev1">
        <span className="inline-flex items-center gap-1 rounded-md bg-neutral-100 px-1.5 py-0.5 text-[11px] font-semibold text-neutral-600" data-id="element-499" data-mp="xcnwafethfz5">
          {deptLabels[order.dept]}
        </span>
      </div>

      {/* Status — wraps instead of overflowing */}
      <div className="mt-2.5" data-id="element-500" data-mp="2rlkplmp9f4c5">
        <span className={`inline-flex max-w-full items-start gap-1 rounded-md px-2 py-1 text-[11px] font-semibold leading-snug ${statusStyles[order.statusTone]}`} data-id="element-501" data-mp="2rmvr1fv8g003">
          {order.overdue ? <AlertTriangleIcon className="mt-px h-3 w-3 shrink-0" data-id="element-502" data-mp="2hteysgaqusyj" /> : order.statusTone === "done" ? <CheckCircle2Icon className="mt-px h-3 w-3 shrink-0" data-id="element-503" data-mp="35j9bmxv2chis" /> : <ClockIcon className="mt-px h-3 w-3 shrink-0" data-id="element-504" data-mp="3ajdq6icev26d" />}
          <span className="break-words" data-id="element-505" data-mp="2wjm74371evli">{order.status}</span>
        </span>
      </div>

      <p className={`mt-2 text-[11px] font-medium ${order.overdue ? "text-red-600" : "text-neutral-400"}`} data-id="element-506" data-mp="30l7pg42cc233">{order.eta}</p>
    </motion.div>;
}